import { lumi } from '@/lib/lumi'

const AUTH_PREFIX = 'Penguin Pad: authorize colony follow'
const AUTH_TTL_MS = 30 * 60 * 1000
const FAVORITE_UPDATED_EVENT = 'penguin-pad-wallet-favorite-updated'

export type WalletFavoriteUpdate = {
  wallet: string
  favoriteWallet: string
  favorited: boolean
}

function normalizeWallet(value?: string) {
  return value?.trim().toLowerCase() || ''
}

function buildAuthMessage(wallet: string) {
  const issued = new Date().toISOString()
  const expires = new Date(Date.now() + AUTH_TTL_MS).toISOString()
  return `${AUTH_PREFIX}\n\nWallet: ${wallet}\nIssued: ${issued}\nExpires: ${expires}`
}

function invokeErrorMessage(error: unknown, fallback: string) {
  const record = error as { data?: { error?: string }, message?: string }
  return (record?.data?.error || record?.message || fallback).slice(0, 180)
}

export async function getFollowStatus(
  favoriteWallet: string,
  viewerWallet?: string,
): Promise<{ favorited: boolean, followersCount: number }> {
  const target = normalizeWallet(favoriteWallet)
  if (!target)
    return { favorited: false, followersCount: 0 }
  try {
    const result = await lumi.functions.invoke('walletFavorites', {
      method: 'POST',
      body: {
        action: 'status',
        favoriteWallet: target,
        wallet: normalizeWallet(viewerWallet) || undefined,
      },
    }) as { ok?: boolean, favorited?: boolean, followersCount?: number } | undefined
    if (!result?.ok)
      return { favorited: false, followersCount: 0 }
    return {
      favorited: Boolean(result.favorited),
      followersCount: Number.isFinite(result.followersCount) ? Number(result.followersCount) : 0,
    }
  }
  catch (error) {
    console.error('Fetch follow status failed:', error)
    return { favorited: false, followersCount: 0 }
  }
}

export async function listFollowing(wallet: string): Promise<string[]> {
  const key = normalizeWallet(wallet)
  if (!key)
    return []
  try {
    const result = await lumi.functions.invoke('walletFavorites', {
      method: 'POST',
      body: { action: 'list', wallet: key },
    }) as { ok?: boolean, favorites?: unknown } | undefined
    if (!result?.ok || !Array.isArray(result.favorites))
      return []
    return result.favorites.filter((item): item is string => typeof item === 'string')
  }
  catch (error) {
    console.error('List following failed:', error)
    return []
  }
}

export async function toggleFollow(input: {
  wallet: string
  favoriteWallet: string
  signMessage: (message: string) => Promise<string>
}): Promise<boolean> {
  const wallet = normalizeWallet(input.wallet)
  const favoriteWallet = normalizeWallet(input.favoriteWallet)
  if (!wallet)
    throw new Error('Connect your wallet to follow Colony members')
  if (!favoriteWallet)
    throw new Error('A wallet to follow is required')
  if (wallet === favoriteWallet)
    throw new Error('You cannot follow your own wallet')

  const message = buildAuthMessage(wallet)
  const signature = (await input.signMessage(message)).trim()
  if (!signature)
    throw new Error('Wallet signature was rejected')

  try {
    const result = await lumi.functions.invoke('walletFavorites', {
      method: 'POST',
      body: { action: 'toggle', wallet, favoriteWallet, message, signature },
    }) as { ok?: boolean, error?: string, favorited?: boolean } | undefined
    if (!result || result.ok === false)
      throw new Error(result?.error || 'Could not update follow status')
    const favorited = Boolean(result.favorited)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent<WalletFavoriteUpdate>(FAVORITE_UPDATED_EVENT, {
        detail: { wallet, favoriteWallet, favorited },
      }))
    }
    return favorited
  }
  catch (error) {
    console.error('Toggle follow failed:', error)
    throw new Error(invokeErrorMessage(error, 'Could not update follow status'))
  }
}

export function onWalletFavoriteUpdated(listener: (event: WalletFavoriteUpdate) => void) {
  if (typeof window === 'undefined')
    return () => {}
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<WalletFavoriteUpdate>).detail
    if (!detail)
      return
    listener(detail)
  }
  window.addEventListener(FAVORITE_UPDATED_EVENT, handler)
  return () => window.removeEventListener(FAVORITE_UPDATED_EVENT, handler)
}
